import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ProjectCard from '../components/ProjectCard';
import { getAllProjects } from '../services/projectService';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAllProjects().then((data) => {
      setProjects(data);
      setLoading(false);
    });
  }, []);

  const statuses = ['All', 'Ongoing', 'Completed', 'Upcoming'];

  const filteredProjects =
    filter === 'All'
      ? projects
      : projects.filter((p) => p.status === filter);

  return (
    <div>
      {/* Page Header */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
            alt="Our Projects"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-charcoal/75" />
        </div>
        <div className="relative z-10 text-center">
          {/* Breadcrumb */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-cream-200/40 font-sans text-xs uppercase tracking-widest-plus mb-4"
          >
            Home &nbsp;/&nbsp; Projects
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4"
          >
            Our Projects
          </motion.h1>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="gold-divider mx-auto mb-4"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-cream-200/60 font-sans text-lg"
          >
            Explore our portfolio of premium developments
          </motion.p>
        </div>
      </section>

      {/* Filter & Projects */}
      <section className="py-24 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs — Underline Style */}
          <div className="flex flex-wrap justify-center gap-1 mb-16 border-b border-charcoal/10 pb-px">
            {statuses.map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className="relative px-8 py-3 text-xs font-sans font-medium uppercase tracking-widest-plus transition-colors duration-300"
              >
                <span className={filter === status ? 'text-gold' : 'text-charcoal/40 hover:text-charcoal/70'}>
                  {status}
                </span>
                {filter === status && (
                  <motion.div
                    layoutId="activeFilter"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-32 h-0.5 bg-cream-200 overflow-hidden rounded">
                <div className="h-full w-1/2 bg-gold animate-shimmer" />
              </div>
              <p className="text-charcoal/30 font-sans text-xs uppercase tracking-widest-plus">Loading</p>
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
            >
              {filteredProjects.map((project, index) => (
                <ProjectCard
                  key={project.id || project._id}
                  project={project}
                  index={index}
                />
              ))}
            </motion.div>
          )}

          {filteredProjects.length === 0 && !loading && (
            <p className="text-center text-charcoal/40 font-sans py-16">
              No projects found in this category.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects;
